import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePageTraffic } from "@/lib/usePageTraffic";
import { ScanLine, FileText, Receipt, Video, Mic, Layers, BarChart3 } from "lucide-react";
import { useClient } from "@/lib/clientContext";
import { useAuth } from "@/lib/auth";
import { UsageStatsPanel } from "@/components/lumina/UsageStatsPanel";
import { getNoteQueueHealth, listNoteSplitBatches } from "@/lib/noteClaim";
import { getEobQueueHealth } from "@/lib/eobEraV2";
import { listAnalyses } from "@/lib/patientDocs";
import { listAppointments } from "@/lib/telehealth";
import { api } from "@/lib/api";
import { relativeTimeAgo } from "@/lib/relativeTime";

const INK = "#141413", IND = "#5B5BD6", HAIR = "#E8E8E5", G9 = "#9A9A96", G6 = "#6C6C69", GREEN = "#1E7F4F";
const WINDOWS = [
  { key: "hour", label: "Last hour", lede: "in the last hour" },
  { key: "today", label: "Today", lede: "today" },
  { key: "week", label: "Last week", lede: "in the last week" },
  { key: "month", label: "This month", lede: "this month" },
  { key: "year", label: "Last 365 days", lede: "in the last 365 days" },
];
const startOf = (key) => {
  const d = new Date();
  if (key === "hour") return Date.now() - 3600e3;
  if (key === "today") { d.setHours(0, 0, 0, 0); return d.getTime(); }
  if (key === "week") return Date.now() - 7 * 864e5;
  if (key === "month") { d.setDate(1); d.setHours(0, 0, 0, 0); return d.getTime(); }
  return Date.now() - 365 * 864e5;
};
const inWin = (iso, from) => { if (!iso) return false; const t = new Date(iso).getTime(); return !isNaN(t) && t >= from; };
const inflightOf = (qh) => ((qh?.jobs?.by_tenant_inflight) || []).reduce((s, t) => s + (t.count ?? t.in_flight ?? 1), 0);
const nAvg = (qh) => { const r = (qh?.jobs?.recent_finished || []).map((x) => x.stage_durations_ms?.total).filter((x) => x > 0); return r.length ? Math.round(r.reduce((a, b) => a + b, 0) / r.length) : null; };
const LANE_BY_AGENT = { "Coding": "coding", "EOB extraction": "eob", "Lens": "lens", "Multi-part splits": "splits", "Dictation": "dict", "Telehealth": "tele" };
const latestBy = (arr, tsOf) => { let best = null; for (const x of (arr || [])) { const t = tsOf(x); if (t && (!best || new Date(t) > new Date(tsOf(best)))) best = x; } return best; };
const secs = (ms) => (ms ? `${(ms / 1000).toFixed(1)}s` : null);

function useCountUp(value, ms = 700) {
  const [disp, setDisp] = useState(value);
  const prev = useRef(value);
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const from = prev.current, to = Number(value) || 0; prev.current = to;
    if (reduce || from === to) { setDisp(to); return undefined; }
    let raf; const t0 = performance.now();
    const tick = (t) => { const p = Math.min(1, (t - t0) / ms); const e = 1 - Math.pow(1 - p, 3); setDisp(Math.round(from + (to - from) * e)); if (p < 1) raf = requestAnimationFrame(tick); };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [value, ms]);
  return disp;
}

export default function LuminaAtWork() {
  const { selectedClientId } = useClient();
  const { clients } = useClient();
  const { user } = useAuth();
  const [statsOpen, setStatsOpen] = useState(false);
  const clientName = useMemo(() => {
    const c = (clients || []).find((x) => String(x.client_id) === String(selectedClientId));
    return c?.description ? c.description : `Client ${selectedClientId || "—"}`;
  }, [clients, selectedClientId]);

  const [win, setWin] = useState("today");
  const [note, setNote] = useState(null);
  const [eob, setEob] = useState(null);
  const [batches, setBatches] = useState([]);
  const [lensTotal, setLensTotal] = useState(0);
  const [ntcBatches, setNtcBatches] = useState([]);
  const [appts, setAppts] = useState(null);
  const [notes, setNotes] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [tick, setTick] = useState(0); // prunes transient flash/fresh state
  const { report: reportTraffic, markSearch: markSearchTraffic } = usePageTraffic("lumina-at-work");
  const lawFirst = useRef(true);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      getNoteQueueHealth().then((d) => alive && setNote(d)).catch(() => {});
      getEobQueueHealth().then((d) => alive && setEob(d)).catch(() => {});
      if (selectedClientId) {
        listNoteSplitBatches({ client_id: selectedClientId }).then((b) => alive && setNtcBatches(b || [])).catch(() => alive && setNtcBatches([]));
        listAppointments(selectedClientId).then((a) => alive && setAppts(Array.isArray(a) ? a : (a?.appointments || []))).catch(() => alive && setAppts(null));
        api.get("/api/edi/note-to-claim/notes", { params: { client_id: selectedClientId, limit: 400, skip: 0 } }).then((r) => alive && setNotes(r.data?.notes || r.data?.items || [])).catch(() => alive && setNotes([]));
      }
      if (alive) setNow(Date.now());
    };
    load();
    const t = setInterval(() => { if (!document.hidden) load(); }, 10000);
    const onVis = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { alive = false; clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, [selectedClientId]);

  // prune transient green states
  useEffect(() => { const t = setInterval(() => setTick((x) => x + 1), 300); return () => clearInterval(t); }, []);

  // Lens docs (/documents/analyses) — page through newest-first until we pass the
  // selected window so wide windows (week/month/year) aren't capped at one page.
  useEffect(() => {
    if (!selectedClientId) { setBatches([]); setLensTotal(0); return undefined; }
    let alive = true;
    const winStart = startOf(win);
    const load = async (isPoll) => {
      if (!isPoll && !lawFirst.current) markSearchTraffic({ window: win });
      try {
        const pageSize = 100, maxPages = 12;
        let skip = 0, acc = [], total = 0;
        for (let p = 0; p < maxPages; p++) {
          const d = await listAnalyses({ client_id: selectedClientId, skip, limit: pageSize });
          const page = d?.analyses || [];
          total = d?.total ?? total;
          acc = acc.concat(page);
          if (page.length < pageSize) break;
          const oldest = page[page.length - 1]?.created_at;
          if (oldest && new Date(oldest).getTime() < winStart) break;
          skip += pageSize;
        }
        if (alive) {
          setBatches(acc); setLensTotal(total);
          if (!isPoll) { if (lawFirst.current) lawFirst.current = false; reportTraffic(total || acc.length); }
        }
      } catch { if (alive) { setBatches([]); setLensTotal(0); } }
    };
    load(false);
    const t = setInterval(() => { if (!document.hidden) load(true); }, 10000);
    return () => { alive = false; clearInterval(t); };
  }, [selectedClientId, win]); // eslint-disable-line react-hooks/exhaustive-deps

  const from = startOf(win);
  const winMeta = WINDOWS.find((w) => w.key === win);

  // ---- tenant-wide stats (same source the old home card used) ----
  const stat = (qh) => {
    const j = qh?.jobs || {}; const c = j.counts || {};
    const recent = j.recent_finished || [];
    const durs = recent.map((r) => r.stage_durations_ms?.total).filter((x) => x > 0);
    return {
      ready: c.ready || 0, failed: c.failed || 0, queued: c.queued || 0,
      workers: j.worker_count ?? 2, cap: j.per_tenant_cap ?? 2, revivals: j.revivals_count || 0,
      inflight: inflightOf(qh), avgMs: durs.length ? Math.round(durs.reduce((a, b) => a + b, 0) / durs.length) : null,
    };
  };
  const n = stat(note), e = stat(eob);
  const passes = n.ready + e.ready;
  const failed = n.failed + e.failed;
  const cleanPct = passes + failed > 0 ? (passes / (passes + failed)) * 100 : 100;
  const tAvg = [n.avgMs, e.avgMs].filter(Boolean);
  const avgMs = tAvg.length ? Math.round(tAvg.reduce((a, b) => a + b, 0) / tAvg.length) : null;
  const stranded = n.revivals + e.revivals;
  const workers = n.workers + e.workers;
  const cap = n.cap;

  // ---- per-agent lanes ----
  const agents = useMemo(() => {
    const inflightStatus = ["processing", "analyzing", "queued", "pending", "running"];
    const lensRunningSegs = batches.filter((b) => inflightStatus.includes(String(b.status || "").toLowerCase())).length;
    const lensQueued = batches.filter((b) => String(b.status || "").toLowerCase() === "queued").length;
    const lensWin = batches.filter((b) => inWin(b.created_at, from)).length;
    const lensAll = lensTotal || batches.length;

    const ntcRunning = ntcBatches.filter((b) => b.status === "processing").length;
    const ntcWin = ntcBatches.filter((b) => inWin(b.created_at, from)).reduce((s, b) => s + (b.created_count ?? (b.segments || []).filter((g) => g.status === "created").length), 0);
    const ntcAll = ntcBatches.reduce((s, b) => s + (b.created_count ?? (b.segments || []).filter((g) => g.status === "created").length), 0);

    const codeWin = (note?.jobs?.recent_finished || []).filter((r) => inWin(r.finished_at_iso, from)).length;
    const eobWin = (eob?.jobs?.recent_finished || []).filter((r) => inWin(r.finished_at_iso, from)).length;
    const codeRun = (note?.jobs?.counts?.processing || 0) || inflightOf(note);
    const eobRun = (eob?.jobs?.counts?.processing || 0) || inflightOf(eob);

    let teleWin = 0, teleRun = 0, teleAll = 0;
    if (Array.isArray(appts)) {
      const done = appts.filter((a) => ["completed", "complete", "done"].includes((a.status || "").toLowerCase()));
      teleAll = done.length;
      teleWin = done.filter((a) => inWin(a.completed_at || a.updated_at || a.ended_at || a.start_time, from)).length;
      teleRun = appts.filter((a) => ["in_visit", "started", "in-progress", "active"].includes((a.status || "").toLowerCase())).length;
    }
    const dictNotes = Array.isArray(notes) ? notes.filter((x) => String(x.visit_source ?? x.source ?? "").toLowerCase().includes("dictation")) : null;
    const dictWin = dictNotes ? dictNotes.filter((x) => inWin(x.created_at || x.date_of_service, from)).length : 0;
    const dictAll = dictNotes ? dictNotes.length : 0;

    return [
      { key: "lens", group: "CLINICAL", icon: ScanLine, name: "Lens", flow: "scan → documents", pooled: true, win: lensWin, done: lensAll, avg: null, inflight: lensRunningSegs, queued: lensQueued },
      { key: "splits", group: "CLINICAL", icon: Layers, name: "Multi-part splits", flow: "bundle → visits", pooled: false, win: ntcWin, done: ntcAll, avg: null, inflight: ntcRunning, queued: 0 },
      { key: "dict", group: "CLINICAL", icon: Mic, name: "Dictations", flow: "voice → note", pooled: false, win: dictWin, done: dictAll, avg: null, inflight: 0, queued: 0, approx: true },
      { key: "tele", group: "CLINICAL", icon: Video, name: "Telehealth visits", flow: "visit → note", pooled: false, win: teleWin, done: teleAll, avg: null, inflight: teleRun, queued: 0, approx: true },
      { key: "coding", group: "BILLING", icon: FileText, name: "Coding", flow: "note → claim", pooled: true, win: codeWin, done: note?.jobs?.counts?.ready ?? 0, avg: nAvg(note), inflight: codeRun, queued: note?.jobs?.counts?.queued || 0 },
      { key: "eob", group: "BILLING", icon: Receipt, name: "EOB extraction", flow: "remit → 835", pooled: true, win: eobWin, done: eob?.jobs?.counts?.ready ?? 0, avg: nAvg(eob), inflight: eobRun, queued: eob?.jobs?.counts?.queued || 0 },
    ].map((a) => ({ ...a, running: a.inflight > 0 || a.queued > 0 }));
  }, [batches, lensTotal, ntcBatches, note, eob, appts, notes, from]);

  const totalWin = agents.reduce((s, a) => s + (a.win || 0), 0);
  const inflightTotal = agents.reduce((s, a) => s + a.inflight, 0);
  const queuedTotal = agents.reduce((s, a) => s + a.queued, 0);
  const anyRunning = agents.some((a) => a.running);
  const bigNum = useCountUp(totalWin);

  // ---- ticker ----
  // Union across every pipeline. Coding/EOB carry a true finished_at; Lens &
  // Multi-part splits use upload time (created_at) as the finish proxy; Dictation
  // & Telehealth contribute their latest start time.
  const ticker = useMemo(() => {
    const mk = (id, filename, finished_at_iso, agent) => ({ id, filename, finished_at_iso, agent });
    const items = [
      ...((note?.jobs?.recent_finished) || []).map((r) => ({ ...r, agent: "Coding" })),
      ...((eob?.jobs?.recent_finished) || []).map((r) => ({ ...r, agent: "EOB extraction" })),
      ...((batches || []).map((b) => mk(`lens:${b.id}`, b.filename || "Document", b.created_at, "Lens"))),
      ...((ntcBatches || []).map((b) => mk(`splits:${b.parent_id || b.id}`, b.filename || "Multi-part bundle", b.created_at, "Multi-part splits"))),
    ];
    const dictNotes = Array.isArray(notes) ? notes.filter((x) => String(x.visit_source ?? x.source ?? "").toLowerCase().includes("dictation")) : [];
    const lastDict = latestBy(dictNotes, (x) => x.created_at || x.date_of_service);
    if (lastDict) items.push(mk(`dict:${lastDict.id || lastDict.note_id || lastDict.created_at}`, lastDict.patient_name || lastDict.patient?.name || lastDict.filename || "Dictation", lastDict.created_at || lastDict.date_of_service, "Dictation"));
    const lastTele = latestBy(Array.isArray(appts) ? appts : [], (a) => a.start_time);
    if (lastTele) items.push(mk(`tele:${lastTele.appointment_id || lastTele.id || lastTele.start_time}`, lastTele.patient_name || lastTele.patient?.name || "Telehealth visit", lastTele.start_time, "Telehealth"));
    return items.sort((a, b) => new Date(b.finished_at_iso || 0) - new Date(a.finished_at_iso || 0)).slice(0, 20);
  }, [note, eob, batches, ntcBatches, notes, appts]);

  // ---- green flash on newly finished ----
  const seenRef = useRef(null);
  const flashRef = useRef({}); // agentKey -> expiry
  const freshRef = useRef({}); // ticker id -> expiry
  useEffect(() => {
    const ids = ticker.map((r) => r.id).filter(Boolean);
    const idSet = new Set(ids);
    if (seenRef.current === null) { seenRef.current = idSet; return; }
    const newly = ticker.filter((r) => r.id && !seenRef.current.has(r.id));
    seenRef.current = idSet;
    if (newly.length) {
      const t = Date.now();
      newly.forEach((r) => {
        freshRef.current[r.id] = t + 2400;
        const k = LANE_BY_AGENT[r.agent] || "coding";
        flashRef.current[k] = t + 1100;
      });
      setTick((x) => x + 1);
    }
  }, [ticker]);
  const laneFlash = (k) => (flashRef.current[k] || 0) > Date.now();
  const isFresh = (id) => (freshRef.current[id] || 0) > Date.now();

  const timeAgo = (iso) => relativeTimeAgo(iso, now);
  const refreshed = new Date(now).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const CSS = `
    .aa-engine{display:grid;grid-template-columns:330px minmax(0,1fr);gap:8px}
    .aa-tenant{display:grid;grid-template-columns:1fr 1fr;gap:14px 16px}
    .aa-colhead,.aa-lane{display:grid;grid-template-columns:168px 1fr 46px 64px;gap:14px;align-items:center}
    @media(max-width:1240px){.aa-engine{grid-template-columns:1fr}.aa-left{border-right:0!important;border-bottom:1px solid ${HAIR}}.aa-tenant{grid-template-columns:repeat(4,1fr)}}
    .aa-thead,.aa-trow{display:grid;grid-template-columns:minmax(210px,1.5fr) minmax(190px,1.3fr) 90px 100px 90px 110px;gap:14px;align-items:center}
    @media(max-width:1180px){.aa-colavg,.aa-colcap{display:none!important}.aa-thead,.aa-trow{grid-template-columns:minmax(210px,1.5fr) minmax(190px,1.3fr) 90px 100px}}
    @keyframes aaRing{0%{transform:scale(.5);opacity:.5}100%{transform:scale(1.5);opacity:0}}
    .aa-ring{animation:aaRing 1.8s ease-out infinite}
    @keyframes aaPulse{0%{opacity:0;left:0}12%{opacity:1}88%{opacity:1}100%{opacity:0;left:calc(100% - 22px)}}
    .aa-pulse{position:absolute;top:50%;margin-top:-3px;width:22px;height:6px;border-radius:3px;background:linear-gradient(90deg,transparent,${IND});animation:aaPulse 2.2s linear infinite}
    @keyframes aaArc{to{transform:rotate(360deg)}}
    .aa-arc{position:absolute;inset:-1px;border-radius:999px;border:1.5px solid transparent;border-top-color:${IND};animation:aaArc 1.1s linear infinite}
    @keyframes aaShim{0%{transform:translateX(-100%)}100%{transform:translateX(320%)}}
    .aa-shim{animation:aaShim 1.4s linear infinite}
    @keyframes aaMarq{from{transform:translateX(0)}to{transform:translateX(-50%)}}
    .aa-belt{display:flex;width:max-content;gap:10px;animation:aaMarq 70s linear infinite}
    .aa-belt:hover{animation-play-state:paused}
    @keyframes aaFresh{0%{background:#E8F5EE;border-color:#A7D8BC}100%{background:#fff;border-color:${HAIR}}}
    .aa-fresh{animation:aaFresh 2.4s ease-out forwards}
    @media(prefers-reduced-motion:reduce){
      .aa-ring,.aa-pulse,.aa-arc,.aa-shim,.aa-fresh{animation:none!important}
      .aa-pulse{display:none}
      .aa-belt{animation:none;overflow-x:auto}
      .aa-scroll{overflow-x:auto}
    }`;

  const Dot = ({ color, pulse }) => (
    <span className="relative inline-flex items-center justify-center flex-none" style={{ width: 8, height: 8 }}>
      {pulse ? <span className="aa-ring absolute rounded-full" style={{ width: 8, height: 8, background: IND }} /> : null}
      <span className="rounded-full" style={{ width: 8, height: 8, background: color }} />
    </span>
  );

  const groups = [["CLINICAL", agents.filter((a) => a.group === "CLINICAL")], ["BILLING", agents.filter((a) => a.group === "BILLING")]];

  return (
    <div style={{ background: "#F6F6F4", minHeight: "100%" }} data-testid="agent-activity-page">
      <style>{CSS}</style>
      <div style={{ maxWidth: 1180, margin: "0 auto", padding: "28px 32px 90px" }}>
        {/* header */}
        <div className="flex items-center flex-wrap" style={{ gap: 24, marginBottom: 16 }}>
          <div className="flex items-center" style={{ gap: 10 }}>
            <img src="/assets/lumina_emblem_black.png" alt="" aria-hidden="true" style={{ width: 22, height: 22, objectFit: "contain" }} data-testid="aa-emblem" />
            <h1 style={{ fontSize: 20, fontWeight: 700, letterSpacing: "-0.01em", color: INK }}>Agent activity</h1>
          </div>
          <div className="flex items-center" style={{ marginLeft: "auto", gap: 12 }}>
            <span style={{ fontSize: 11.5, color: G9 }}>Today and live figures are exact</span>
            <button type="button" onClick={() => setStatsOpen(true)} data-testid="aa-usage-stats-btn"
              className="inline-flex items-center" style={{ height: 36, padding: "0 14px", gap: 8, borderRadius: 10, border: `1px solid ${HAIR}`, background: "#fff", fontSize: 12.5, fontWeight: 600, color: INK }}>
              <BarChart3 size={15} style={{ color: IND }} /> Usage Indicators &amp; Support Debug
            </button>
            <div className="flex" style={{ background: "#EDEDEB", borderRadius: 10, padding: 3, gap: 2 }} data-testid="aa-range">
              {WINDOWS.map((w) => {
                const on = win === w.key;
                return (
                  <button key={w.key} type="button" onClick={() => setWin(w.key)} data-testid={`aa-range-${w.key}`}
                    style={{ height: 30, padding: "0 12px", fontSize: 12.5, borderRadius: 8, fontWeight: on ? 600 : 500, color: on ? INK : G6, background: on ? "#fff" : "transparent", boxShadow: on ? "0 1px 2px rgba(0,0,0,.08)" : "none" }}>
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* engine card */}
        <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, overflow: "hidden" }} data-testid="aa-engine-card">
          <div className="aa-engine">
            {/* left hero */}
            <div className="aa-left flex flex-col" style={{ padding: "26px 26px 22px", borderRight: `1px solid ${HAIR}`, gap: 14 }}>
              <span className="inline-flex items-center self-start" style={{ height: 26, padding: "0 11px", borderRadius: 99, fontSize: 12, fontWeight: 600, gap: 8, background: anyRunning ? "#EEEEFB" : "#F1F1F4", color: anyRunning ? "#4A4AC2" : G6 }} data-testid="aa-status-pill">
                <Dot color={anyRunning ? IND : "#C4C4C0"} pulse={anyRunning} />
                {anyRunning ? `Working · ${inflightTotal} in flight · ${queuedTotal} queued` : `Idle · last finished ${ticker[0] ? timeAgo(ticker[0].finished_at_iso) : "—"}`}
              </span>
              <div style={{ fontSize: 76, fontWeight: 700, letterSpacing: "-0.01em", lineHeight: 0.9, fontVariantNumeric: "tabular-nums", color: INK }} data-testid="aa-bignum">{bigNum}</div>
              <div style={{ fontSize: 14, color: G6, lineHeight: 1.45 }}>
                items handled for <b style={{ color: INK }}>{clientName}</b> {winMeta.lede}, without anyone asking.
              </div>
              <div className="aa-tenant" style={{ marginTop: "auto", paddingTop: 16, borderTop: `1px solid ${HAIR}` }}>
                {[
                  ["Successful passes", String(passes), false],
                  ["Clean first time", `${cleanPct.toFixed(1)}%`, true],
                  ["Average per document", avgMs ? `${(avgMs / 1000).toFixed(0)}s` : "—", false],
                  ["Stranded", stranded ? String(stranded) : "None", false],
                ].map(([label, val, derived]) => (
                  <div key={label}>
                    <div className="flex items-center" style={{ gap: 5, fontSize: 11, color: G9, whiteSpace: "nowrap" }}>{label}{derived ? <span style={{ fontSize: 10, border: `1px solid ${HAIR}`, borderRadius: 4, padding: "0 3px" }}>derived</span> : null}</div>
                    <div style={{ fontSize: 19, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: INK }}>{val}</div>
                  </div>
                ))}
                <div style={{ gridColumn: "1 / -1", fontSize: 11, color: G9 }}>All clients in this tenant · refreshed {refreshed}</div>
              </div>
            </div>

            {/* right lanes */}
            <div className="flex flex-col" style={{ padding: "18px 24px 14px 18px" }}>
              <div className="aa-colhead" style={{ fontSize: 11, color: G9, paddingBottom: 8 }}>
                <span>Agent</span><span>In flight</span><span style={{ textAlign: "center" }}>Workers</span><span style={{ textAlign: "right" }}>{winMeta.label}</span>
              </div>
              {groups.map(([gname, list]) => (
                <React.Fragment key={gname}>
                  <div style={{ fontSize: 10.5, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", color: G9, padding: "10px 0 2px" }}>{gname}</div>
                  {list.map((a) => {
                    const Icon = a.icon;
                    const flash = laneFlash(a.key);
                    const busySlots = a.pooled ? Math.min(a.inflight, 2) : (a.running ? 1 : 0);
                    const slots = a.pooled ? 2 : 1;
                    const pulses = Math.min(a.inflight + a.queued, 4);
                    return (
                      <div key={a.key} className="aa-lane" style={{ height: 46 }} data-testid={`aa-lane-${a.key}`}>
                        {/* name block */}
                        <div className="flex items-center" style={{ gap: 10, minWidth: 0 }}>
                          <span className="inline-flex items-center justify-center flex-none" style={{ width: 28, height: 28, borderRadius: 8, background: a.running ? "#EEEEFB" : "#F3F3F1" }}>
                            <Icon size={16} style={{ color: a.running ? IND : G6 }} />
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate" style={{ fontSize: 13, fontWeight: 600, color: INK }}>{a.name}</span>
                            <span className="block truncate" style={{ fontSize: 11, color: G9 }}>{a.flow}</span>
                          </span>
                        </div>
                        {/* track */}
                        <div style={{ position: "relative", height: 22 }}>
                          <div style={{ position: "absolute", top: "50%", left: 0, right: 0, height: 1, background: a.running ? "linear-gradient(90deg,rgba(91,91,214,0),rgba(91,91,214,.35))" : HAIR }} />
                          {a.running ? Array.from({ length: pulses }).map((_, i) => (
                            <span key={i} className="aa-pulse" style={{ animationDelay: `${i * 0.35}s`, animationDuration: `${2.2 + i * 0.35}s` }} />
                          )) : null}
                        </div>
                        {/* node */}
                        <div className="relative mx-auto" style={{ width: 38, height: 38, borderRadius: 999, border: `1px solid ${flash ? GREEN : (a.running ? "rgba(91,91,214,.45)" : HAIR)}`, display: "grid", placeItems: "center" }}>
                          {a.running && !flash ? <span className="aa-arc" /> : null}
                          <span className="flex" style={{ gap: 4 }}>
                            {Array.from({ length: slots }).map((_, i) => (
                              <span key={i} className="rounded-full" style={{ width: 6, height: 6, background: i < busySlots ? IND : "#E3E3E0" }} />
                            ))}
                          </span>
                        </div>
                        {/* count */}
                        <div style={{ textAlign: "right", fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: flash ? GREEN : INK }}>
                          {a.win}{a.approx ? <sup style={{ fontSize: 11, color: G9, marginLeft: 1 }}>≈</sup> : null}
                        </div>
                      </div>
                    );
                  })}
                </React.Fragment>
              ))}
              <div style={{ marginTop: "auto", paddingTop: 10, fontSize: 11, color: G9 }}>{workers} workers · per-tenant cap {cap} · two pipelines polled every 60s</div>
            </div>
          </div>

          {/* ticker */}
          <div className="flex items-stretch" style={{ height: 52, borderTop: `1px solid ${HAIR}`, background: "#fff" }} data-testid="aa-ticker">
            <div className="flex items-center flex-none" style={{ padding: "0 16px 0 22px", borderRight: `1px solid ${HAIR}` }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: INK }}>Just finished</span>
              <span style={{ fontSize: 12, color: G9, marginLeft: 4 }}>· hover to pause</span>
            </div>
            <div className="aa-scroll relative flex-1 overflow-hidden" style={{ WebkitMaskImage: "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)", maskImage: "linear-gradient(90deg,transparent,#000 6%,#000 94%,transparent)" }}>
              {ticker.length === 0 ? (
                <div className="flex items-center h-full px-4" style={{ fontSize: 12, color: G9 }}>Nothing finished recently.</div>
              ) : (
                <div className="aa-belt h-full items-center">
                  {[...ticker, ...ticker].map((r, i) => {
                    const fresh = i < ticker.length && isFresh(r.id);
                    return (
                      <span key={i} className={`inline-flex items-center flex-none ${fresh ? "aa-fresh" : ""}`} style={{ height: 30, padding: "0 12px", borderRadius: 99, background: "#fff", border: `1px solid ${HAIR}`, fontSize: 12, color: G6, gap: 8, whiteSpace: "nowrap" }} data-testid={i < ticker.length ? `aa-tick-${i}` : undefined}>
                        <span className="rounded-full flex-none" style={{ width: 6, height: 6, background: fresh ? GREEN : "#C4C4C0" }} />
                        <b style={{ color: INK, fontWeight: 600, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis" }}>{r.filename}</b>
                        <span>· {r.agent}</span>
                        <span style={{ color: G9 }}>· {timeAgo(r.finished_at_iso)}</span>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* by agent table */}
        <div style={{ background: "#fff", border: `1px solid ${HAIR}`, borderRadius: 14, marginTop: 16 }} data-testid="aa-table">
          <div className="flex items-baseline flex-wrap" style={{ gap: 8, padding: "14px 18px", borderBottom: `1px solid ${HAIR}` }}>
            <span style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>By agent</span>
            <span style={{ fontSize: 12, color: G9 }}>counts for {clientName} · capacity shared across all clients</span>
          </div>
          <div style={{ padding: "0 18px" }}>
            <div className="aa-thead" style={{ height: 34, fontSize: 11.5, color: G9, borderBottom: `1px solid ${HAIR}` }}>
              <span>Agent</span><span>Right now</span><span style={{ textAlign: "right" }}>{winMeta.label}</span><span style={{ textAlign: "right" }}>Done so far</span><span className="aa-colavg" style={{ textAlign: "right" }}>Avg time</span><span className="aa-colcap">Capacity</span>
            </div>
            {groups.map(([gname, list]) => (
              <React.Fragment key={gname}>
                <div style={{ fontSize: 11, fontWeight: 600, color: G9, padding: "12px 0 4px" }}>{gname === "CLINICAL" ? <>Clinical · <span style={{ fontWeight: 400, color: "#C4C4C0" }}>Lumina</span></> : <>Billing · <span style={{ fontWeight: 400, color: "#C4C4C0" }}>revQ</span></>}</div>
                {list.map((a) => {
                  const Icon = a.icon;
                  const busySlots = a.pooled ? Math.min(a.inflight, 2) : (a.running ? 1 : 0);
                  const desc = { lens: "documents read and sorted from uploads", splits: "bundles split into separate visits", dict: "notes captured by voice", tele: "visits the AI supported", coding: "notes turned into claims", eob: "remittances turned into 835s" }[a.key];
                  return (
                    <div key={a.key} className="aa-trow" style={{ minHeight: 60, borderBottom: "1px solid #F0F0EE" }} data-testid={`aa-row-${a.key}`}>
                      <div className="flex items-center" style={{ gap: 10, minWidth: 0 }}>
                        <span className="inline-flex items-center justify-center flex-none" style={{ width: 32, height: 32, borderRadius: 9, background: "#F3F3F1" }}><Icon size={16} style={{ color: G6 }} /></span>
                        <span className="min-w-0"><span className="block truncate" style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>{a.name}</span><span className="block truncate" style={{ fontSize: 12, color: G9 }}>{desc}</span></span>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        {a.running ? (
                          <>
                            <div className="flex items-center" style={{ gap: 6, fontSize: 12.5, fontWeight: 600, color: IND }}>
                              <Dot color={IND} pulse />{a.inflight} in progress{a.queued > 0 ? ` · ${a.queued} queued` : ""}
                            </div>
                            <div className="relative overflow-hidden" style={{ height: 3, maxWidth: 180, borderRadius: 2, background: "#ECECF9", marginTop: 6 }}>
                              <span className="aa-shim absolute top-0 left-0 h-full" style={{ width: "40%", background: IND, borderRadius: 2 }} />
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center" style={{ gap: 6, fontSize: 12.5, color: G6 }}><Dot color="#C4C4C0" />Idle</div>
                        )}
                      </div>
                      <div style={{ textAlign: "right", fontSize: 20, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: INK }}>{a.win}{a.approx ? <sup style={{ fontSize: 11, color: G9 }}>≈</sup> : null}</div>
                      <div style={{ textAlign: "right", fontSize: 12.5, color: G6, fontVariantNumeric: "tabular-nums" }}>{a.done}</div>
                      <div className="aa-colavg" style={{ textAlign: "right", fontSize: 12.5, color: a.avg ? G6 : "#C4C4C0" }}>{secs(a.avg) || "—"}</div>
                      <div className="aa-colcap">
                        {a.pooled ? (
                          <div className="flex items-center" style={{ gap: 6 }}>
                            <span className="flex" style={{ gap: 3 }}>{[0, 1].map((i) => <span key={i} className="rounded-full" style={{ width: 7, height: 7, background: i < busySlots ? IND : "transparent", border: i < busySlots ? "none" : "1px solid #C4C4C0" }} />)}</span>
                            <span style={{ fontSize: 12, color: G9 }}>{busySlots} of 2 workers</span>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
          </div>
          <div style={{ fontSize: 11.5, color: G9, padding: "10px 18px", borderTop: `1px solid ${HAIR}` }}>≈ Telehealth and dictation counts are best-effort.</div>
        </div>
      </div>
      <UsageStatsPanel open={statsOpen} onClose={() => setStatsOpen(false)} email={user?.email || ""} />
    </div>
  );
}
